import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, Calendar as CalIcon, ListChecks, Plus, Folder,
  RotateCcw, CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { projectsApi, Project } from '../../api/projects';
import ConfirmDialog, { type ConfirmState } from '../../components/ConfirmDialog';
import { Task } from '../../api/tasks';
import TaskCard from '../../components/TaskCard';
import TaskCreate from '../Task/TaskCreate';

// Mirrors the Tasks tab so the project view paginates the same way the
// global Tasks list does — 9 cards per page (3×3 on lg, 2×N on md).
const TASKS_PAGE_SIZE = 9;

const STATUS_META: Record<string, { color: string; bg: string; label: string }> = {
  ACTIVE:    { color: '#16a34a', bg: 'rgba(22,163,74,0.10)',  label: 'Active' },
  ON_HOLD:   { color: '#d97706', bg: 'rgba(217,119,6,0.10)',  label: 'On Hold' },
  COMPLETED: { color: '#2563eb', bg: 'rgba(37,99,235,0.10)',  label: 'Completed' },
  ARCHIVED:  { color: '#6b7280', bg: 'rgba(107,114,128,0.10)', label: 'Archived' },
};

export default function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeClosed, setIncludeClosed] = useState(true);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Snap back to page 1 whenever the underlying list changes (filter
  // toggle, reload). Without this, toggling "Show closed" on page 3 of
  // a 4-page list can leave the user on an empty page 3 of a now
  // 1-page list.
  useEffect(() => {
    setCurrentPage(1);
  }, [includeClosed, tasks.length]);

  const totalPages = Math.max(1, Math.ceil(tasks.length / TASKS_PAGE_SIZE));
  const visibleTasks = useMemo(
    () =>
      tasks.slice(
        (currentPage - 1) * TASKS_PAGE_SIZE,
        currentPage * TASKS_PAGE_SIZE,
      ),
    [tasks, currentPage],
  );
  const rangeStart = tasks.length === 0 ? 0 : (currentPage - 1) * TASKS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * TASKS_PAGE_SIZE, tasks.length);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [pRes, tRes] = await Promise.all([
        projectsApi.get(id),
        projectsApi.listTasks(id, includeClosed),
      ]);
      setProject(pRes.data?.project || null);
      setTasks(tRes.data?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id, includeClosed]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="gt-loading-ring mx-auto mb-3" />
          <p className="gt-page-sub gt-serif-italic">Loading <em>project</em>…</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-md text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
          {error
            ? <AlertTriangle className="w-9 h-9 mx-auto mb-3" style={{ color: 'var(--gt-dnd)' }} />
            : <Folder className="w-10 h-10 mx-auto mb-3 text-gray-400" />}
          <p className="gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)' }}>
            {error || 'Project not found'}
          </p>
          <button onClick={() => navigate('/projects')} className="gt-btn is-primary mt-4 mx-auto">
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[project.status] || STATUS_META.ACTIVE;
  const counts = project.task_counts || {};
  const total = counts.total || 0;
  const open = counts.open || 0;
  const closed = counts.closed ?? (total - open);
  const pct = total > 0 ? Math.round((closed / total) * 100) : 0;
  const isClosed = project.status === 'ARCHIVED' || project.status === 'COMPLETED';

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        {/* Back */}
        <button
          onClick={() => navigate('/projects')}
          className="inline-flex items-center gap-1.5 gt-page-sub hover:text-gray-900"
          style={{ fontSize: 13 }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Projects
        </button>

        {/* Header */}
        <div className="gt-card p-6" style={{ borderLeft: `3px solid ${meta.color}` }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="gt-eyebrow-row" style={{ marginBottom: 8 }}>
                <span className="gt-leaf" />
                <span className="gt-eyebrow">Project</span>
              </div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h1 className="gt-page-title" style={{ fontSize: 28 }}>{project.name}</h1>
                <span
                  className="text-[10px] uppercase tracking-wide px-2.5 py-1 rounded-full font-bold"
                  style={{ background: meta.bg, color: meta.color }}
                >
                  {meta.label}
                </span>
                {project.code && (
                  <span className="text-xs gt-numerals px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                    {project.code}
                  </span>
                )}
              </div>
              {project.description && (
                <p className="gt-page-sub mb-3" style={{ fontSize: 14 }}>{project.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-4">
                {project.client_name && (
                  <div
                    className="inline-flex items-center gap-1.5"
                    style={{
                      padding: '4px 10px',
                      background: 'var(--gt-primary-container)',
                      color: 'var(--gt-primary-deep)',
                      borderRadius: 6,
                      border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)',
                      fontFamily: 'var(--gt-font-display)',
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  >
                    <Briefcase className="w-3 h-3" />
                    <span>{project.client_name}</span>
                  </div>
                )}
                {project.vendor_name && (
                  <div
                    className="inline-flex items-center gap-1.5"
                    style={{
                      padding: '4px 10px',
                      background: 'var(--gt-accent-container, #fef3c7)',
                      color: 'var(--gt-accent-deep, #92400e)',
                      borderRadius: 6,
                      border: '1px solid color-mix(in srgb, #f59e0b 25%, transparent)',
                      fontFamily: 'var(--gt-font-display)',
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                    title="Vendor"
                  >
                    <Briefcase className="w-3 h-3" />
                    <span>{project.vendor_name}</span>
                  </div>
                )}
                {(project.start_date || project.end_date) && (
                  <div className="flex items-center gap-1.5 gt-page-sub gt-numerals" style={{ fontSize: 12 }}>
                    <CalIcon className="w-3.5 h-3.5" />
                    <span>{project.start_date || '...'} → {project.end_date || '...'}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {isClosed ? (
                <button
                  onClick={async () => {
                    try { await projectsApi.reopen(project.id); await load(); }
                    catch { alert('Failed to reopen'); }
                  }}
                  className="gt-btn is-accent"
                >
                  <RotateCcw className="w-4 h-4" /> Reopen
                </button>
              ) : (
                <button
                  onClick={() => {
                    setConfirm({
                      title: 'Mark as completed?',
                      description: <>Project <strong>"{project.name}"</strong> will be marked Completed.</>,
                      confirmLabel: 'Mark Completed',
                      tone: 'success',
                      onConfirm: async () => {
                        try { await projectsApi.complete(project.id); await load(); }
                        catch (e: any) { alert(e?.response?.data?.detail || 'Failed to complete'); throw e; }
                      },
                    });
                  }}
                  className="gt-btn is-accent"
                >
                  <CheckCircle2 className="w-4 h-4" /> Mark Completed
                </button>
              )}
              <button onClick={() => setShowCreateTask(true)} className="gt-btn is-primary">
                <Plus className="w-4 h-4" /> Add Task
              </button>
            </div>
          </div>

          {/* Progress bar */}
          {total > 0 && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Overall Progress</span>
                <span className="gt-display gt-numerals" style={{ fontSize: 13, color: 'var(--gt-text-primary)' }}>
                  {pct}%
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--gt-surface-2)' }}>
                <div
                  className="h-full transition-all"
                  style={{ width: `${pct}%`, background: meta.color }}
                />
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="gt-stat">
              <div className="gt-stat-label"><ListChecks className="w-3.5 h-3.5" /> Total Tasks</div>
              <p className="gt-stat-value">{total}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><AlertTriangle className="w-3.5 h-3.5" /> Open</div>
              <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>{open}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><CheckCircle2 className="w-3.5 h-3.5" /> Closed</div>
              <p className="gt-stat-value" style={{ color: '#16a34a' }}>{closed}</p>
            </div>
          </div>
        </div>

        {/* Tasks heading */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="gt-display flex items-center gap-2" style={{ fontSize: 18, color: 'var(--gt-text-primary)' }}>
            <ListChecks className="w-5 h-5" /> Tasks
          </h2>
          <label className="flex items-center gap-2 gt-page-sub" style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={e => setIncludeClosed(e.target.checked)}
            />
            Show closed
          </label>
        </div>

        {tasks.length === 0 ? (
          <div className="gt-card p-12 text-center">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><ListChecks className="w-6 h-6" /></div>
              <div className="gt-empty-title">No tasks in this project</div>
              <div className="gt-page-sub">
                Add a task to start <em className="gt-serif-italic">tracking</em> work.
              </div>
              <button onClick={() => setShowCreateTask(true)} className="gt-btn is-primary mt-5 mx-auto">
                <Plus className="w-4 h-4" /> Add Task
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Showing X–Y of Z (mirrors the Tasks tab) */}
            <div className="flex justify-between items-center">
              <p className="gt-page-sub gt-numerals">
                Showing{' '}
                <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>
                  {rangeStart}–{rangeEnd}
                </span>{' '}
                of{' '}
                <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>
                  {tasks.length}
                </span>{' '}
                tasks
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleTasks.map(t => <TaskCard key={t.id} task={t} onRefresh={load} />)}
            </div>

            {totalPages > 1 && (
              <>
                <div className="mt-8 flex justify-center items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="gt-btn is-ghost is-sm"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>

                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={currentPage === pageNum ? 'gt-btn is-primary is-sm' : 'gt-btn is-ghost is-sm'}
                          style={{ width: 36, height: 36, justifyContent: 'center', padding: 0 }}
                        >
                          <span className="gt-numerals">{pageNum}</span>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="gt-btn is-ghost is-sm"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="text-center gt-page-sub gt-numerals md:hidden">
                  Page {currentPage} of {totalPages}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <TaskCreate
        isOpen={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        onSuccess={load}
        defaultProjectId={project.id}
        defaultClientId={project.client_id || undefined}
      />

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
